import { PageModularParent } from "@carfax/contentful/PageModularModule";
import { IBreadcrumbItem } from "types/components";

export const CARFAX_BASE_URL = "https://www.carfax.eu";
const MAX_DEPTH = 10;

interface ResolvedSlug {
  href: string;
  language?: string;
}

export const FALLBACK_LANGUAGE = "en";

export const resolveSlugForLang = (slugs: string[], lang: string): ResolvedSlug => {
  const langPrefix = `${lang}/`;
  const matchingSlug = slugs.find((s) => s.startsWith(langPrefix));
  if (matchingSlug) return { href: `/${matchingSlug}`, language: lang };

  const englishSlug = slugs.find((s) => !s.includes("/"));
  if (englishSlug) return { href: `/${englishSlug}`, language: FALLBACK_LANGUAGE };

  const fallback = slugs[0] || "";
  const slashIndex = fallback.indexOf("/");
  if (slashIndex !== -1) {
    const fallbackLang = fallback.slice(0, slashIndex);
    return { href: `/${fallback}`, language: fallbackLang };
  }
  return { href: `/${fallback}`, language: FALLBACK_LANGUAGE };
};

interface BuildBreadcrumbsParams {
  currentPageTitle: string;
  currentPageSlug: string;
  parentPage?: PageModularParent;
  homeLabel: string;
  lang: string;
}

const isHomepage = (slug: string): boolean => slug === "/" || /^[a-z]{2}$/.test(slug);

export const buildModularPageBreadcrumbs = ({
  currentPageTitle,
  currentPageSlug,
  parentPage,
  homeLabel,
  lang,
}: BuildBreadcrumbsParams): IBreadcrumbItem[] => {
  if (isHomepage(currentPageSlug)) {
    return [];
  }

  const trail: IBreadcrumbItem[] = [];
  const visited = new Set<string>();

  let current = parentPage;
  let depth = 0;

  while (current && depth < MAX_DEPTH) {
    const slugKey = current.slugs.join(",");
    if (visited.has(slugKey)) {
      break;
    }
    visited.add(slugKey);

    const resolved = resolveSlugForLang(current.slugs, lang);
    trail.unshift({
      title: current.breadcrumbLabel || current.title,
      href: resolved.href,
      language: resolved.language,
    });

    current = current.parentPage;
    depth++;
  }

  trail.unshift({
    title: homeLabel,
    href: "/",
  });

  trail.push({
    title: currentPageTitle,
    href: `/${currentPageSlug}`,
  });

  return trail;
};

export const toAbsoluteBreadcrumbs = (breadcrumbs: IBreadcrumbItem[]): IBreadcrumbItem[] =>
  breadcrumbs.map((crumb) => ({
    ...crumb,
    href: `${CARFAX_BASE_URL}${crumb.href}`,
  }));
